import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { Prose } from '@workspace/seedforge-ui/components/prose'
import type { PitchEvaluationViewProps } from '@workspace/seedforge-ui/evaluation'
import { PitchEvaluationView } from '@workspace/seedforge-ui/evaluation'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@workspace/ui/components/alert-dialog'
import { Badge } from '@workspace/ui/components/badge'
import { Button } from '@workspace/ui/components/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@workspace/ui/components/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@workspace/ui/components/dropdown-menu'
import { CtaButton } from '@workspace/ui/components/dsm/cta-button'
import { SecondaryButton } from '@workspace/ui/components/dsm/secondary-button'
import { Input } from '@workspace/ui/components/input'
import { cn } from '@workspace/ui/lib/utils'
import { Check, FileText, Loader2, MoreVertical, Play, RefreshCw, Trash2 } from 'lucide-react'
import { useEffect, useState } from 'react'
import { VideoPreviewModal } from '../../../components/video-preview-modal'
import {
  type AdminEvaluationDetails,
  type AdminEvaluationPitchSession,
  type AdminMetricEvaluation,
  type AdminPersonaEvaluation,
  useAdminDeleteEvaluationMutation,
  useAdminDeletePitchSessionMutation,
  useAdminDocumentDownloadUrlMutation,
  useAdminEvaluationDetailsQuery,
  useAdminEvaluationViewQuery,
  useAdminInvestorsQuery,
  useAdminPitchVideoUrlMutation,
  useAdminRegenerateEvaluationMutation,
} from '../../../features/admin/hooks/use-admin-api'
import {
  DocumentPreviewModal,
  type PreviewDocument,
} from '../../../features/preview/components/document-preview-modal'
import { useVideoPreview } from '../../../hooks/use-video-preview'

function useDebounce<T>(value: T, delay = 300): T {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay)
    return () => clearTimeout(t)
  }, [value, delay])
  return debounced
}

export const Route = createFileRoute('/admin/evaluations/$id')({
  component: AdminEvaluationDetailPage,
})

type Tab = 'overview' | 'pitch-session' | 'research'

function formatDateTime(date: string | Date | null | undefined): string {
  if (!date) return '\u2014'
  return new Date(date).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function scoreColor(score: number | null | undefined): string {
  if (score == null) return 'text-muted-foreground'
  if (score >= 75) return 'text-emerald-400'
  if (score >= 50) return 'text-amber-400'
  return 'text-red-400'
}

const STATUS_VARIANT: Record<string, 'success' | 'warning' | 'secondary' | 'destructive'> = {
  COMPLETED: 'success',
  PROCESSING: 'warning',
  PENDING: 'secondary',
  ACTIVE: 'warning',
  IN_PROGRESS: 'warning',
  INCOMPLETE: 'warning',
  FAILED: 'destructive',
}

/**
 * Whether a pitch recording can be played in the admin view. videoKey may be missing
 * even for COMPLETED sessions (e.g. the browser closed and the engine flipped the
 * status on transcript arrival) — the backend resolves the recording from the
 * deterministic storage path for admins, so any finished session is worth trying.
 */
function hasRecording(ps?: { videoKey: string | null; status: string } | null): boolean {
  return !!ps?.videoKey || ps?.status === 'INCOMPLETE' || ps?.status === 'COMPLETED'
}

function AdminEvaluationDetailPage() {
  const { id } = Route.useParams()
  const navigate = useNavigate()
  const viewQuery = useAdminEvaluationViewQuery(id)
  const detailsQuery = useAdminEvaluationDetailsQuery(id)
  const [activeTab, setActiveTab] = useState<Tab>('overview')
  const [runDialogOpen, setRunDialogOpen] = useState(false)
  const [selectedInvestorId, setSelectedInvestorId] = useState<string | undefined>(undefined)
  const [investorSearch, setInvestorSearch] = useState('')
  const debouncedInvestorSearch = useDebounce(investorSearch, 300)
  const regenerateMutation = useAdminRegenerateEvaluationMutation(id)
  const deletePitchSessionMutation = useAdminDeletePitchSessionMutation()
  const deleteEvaluationMutation = useAdminDeleteEvaluationMutation()
  const [deletePitchSessionOpen, setDeletePitchSessionOpen] = useState(false)
  const [deleteEvaluationOpen, setDeleteEvaluationOpen] = useState(false)
  const investorsQuery = useAdminInvestorsQuery({
    hasPersona: true,
    limit: 50,
    search: debouncedInvestorSearch || undefined,
  })

  const detailsInvestorId = (detailsQuery.data as { data?: AdminEvaluationDetails } | undefined)
    ?.data?.investorId

  useEffect(() => {
    if (runDialogOpen && detailsInvestorId) {
      setSelectedInvestorId(detailsInvestorId)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runDialogOpen, detailsInvestorId])

  if (viewQuery.isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="size-6 animate-spin text-muted-foreground" />
      </div>
    )
  }

  type EvalViewData = PitchEvaluationViewProps & { date: string }
  const viewData = viewQuery.data as { data?: EvalViewData } | undefined
  if (viewQuery.error || !viewData?.data) {
    return (
      <div className="flex h-64 items-center justify-center text-muted-foreground">
        Failed to load evaluation
      </div>
    )
  }

  const evaluation = viewData.data
  const details = (detailsQuery.data as { data?: AdminEvaluationDetails } | undefined)?.data
  const pitchSession = details?.pitchSession

  const isPitchSession = !!pitchSession
  const tabs: { key: Tab; label: string }[] = [
    { key: 'overview', label: 'Overview' },
    { key: 'pitch-session', label: isPitchSession ? 'Pitch Session' : 'Details' },
    ...(isPitchSession ? [{ key: 'research' as Tab, label: 'Deep Research' }] : []),
  ]

  const dateStr = new Date(evaluation.date).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })

  const handleRegenerate = () => {
    regenerateMutation.mutate(
      { path: { id }, body: { investorId: selectedInvestorId } },
      {
        onSuccess: () => {
          setRunDialogOpen(false)
          setSelectedInvestorId(undefined)
          navigate({ to: '/admin/evaluations' })
        },
      }
    )
  }

  return (
    <div className="w-full space-y-4">
      <div className="border-b border-border">
        <div className="-mb-px flex items-center justify-between">
          <div className="flex gap-1">
            {tabs.map((t) => (
              <button
                key={t.key}
                type="button"
                onClick={() => setActiveTab(t.key)}
                className={cn(
                  'border-b-2 px-3 py-2 text-sm font-medium transition-colors',
                  activeTab === t.key
                    ? 'border-orange-500 text-foreground'
                    : 'border-transparent text-muted-foreground hover:text-foreground'
                )}
              >
                {t.label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-1.5">
            <Dialog
              open={runDialogOpen}
              onOpenChange={(open) => {
                setRunDialogOpen(open)
                if (!open) {
                  setSelectedInvestorId(undefined)
                  setInvestorSearch('')
                }
              }}
            >
              <DialogTrigger asChild>
                <Button variant="ghost" size="sm" className="shrink-0 text-muted-foreground">
                  <RefreshCw className="mr-1.5 size-4" />
                  Regenerate
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Run Evaluation</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <p className="text-sm text-muted-foreground">
                      Select an investor to run a custom persona evaluation, or leave empty to run
                      the default council evaluation.
                    </p>
                    <Input
                      placeholder="Search investors..."
                      value={investorSearch}
                      onChange={(e) => setInvestorSearch(e.target.value)}
                      disabled={investorsQuery.isPending}
                    />
                    <div className="max-h-48 overflow-y-auto rounded-md border border-border text-sm">
                      <button
                        type="button"
                        onClick={() => setSelectedInvestorId(undefined)}
                        className={cn(
                          'flex w-full items-center justify-between px-3 py-2 text-left hover:bg-muted/50',
                          !selectedInvestorId && 'bg-muted font-medium'
                        )}
                      >
                        Council (default)
                        {!selectedInvestorId && <Check className="size-3.5 text-primary" />}
                      </button>
                      {investorsQuery.isPending ? (
                        <div className="flex items-center gap-2 px-3 py-2 text-muted-foreground">
                          <Loader2 className="size-3.5 animate-spin" />
                          Loading...
                        </div>
                      ) : (
                        (investorsQuery.data?.data?.items ?? []).map((investor) => (
                          <button
                            key={investor.id}
                            type="button"
                            onClick={() => setSelectedInvestorId(investor.id)}
                            className={cn(
                              'flex w-full items-center justify-between px-3 py-2 text-left hover:bg-muted/50',
                              selectedInvestorId === investor.id && 'bg-muted font-medium'
                            )}
                          >
                            {investor.name}
                            {selectedInvestorId === investor.id && (
                              <Check className="size-3.5 text-primary" />
                            )}
                          </button>
                        ))
                      )}
                    </div>
                  </div>
                  <CtaButton
                    className="w-full"
                    loading={regenerateMutation.isPending}
                    onClick={handleRegenerate}
                  >
                    {selectedInvestorId ? 'Run investor evaluation' : 'Run council evaluation'}
                  </CtaButton>
                </div>
              </DialogContent>
            </Dialog>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="size-8 p-0">
                  <MoreVertical className="size-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {details?.pitchSession && (
                  <DropdownMenuItem
                    className="text-destructive focus:text-destructive"
                    onSelect={() => setDeletePitchSessionOpen(true)}
                  >
                    <Trash2 className="mr-2 size-4" />
                    Delete Pitch Session
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem
                  className="text-destructive focus:text-destructive"
                  onSelect={() => setDeleteEvaluationOpen(true)}
                >
                  <Trash2 className="mr-2 size-4" />
                  Delete Evaluation
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <AlertDialog open={deletePitchSessionOpen} onOpenChange={setDeletePitchSessionOpen}>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete Pitch Session</AlertDialogTitle>
                  <AlertDialogDescription>
                    This will permanently delete the pitch session and all its evaluations. This
                    action cannot be undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={() => {
                      if (!details?.pitchSession) return
                      deletePitchSessionMutation.mutate(
                        { path: { id: details.pitchSession.id } },
                        { onSuccess: () => navigate({ to: '/admin/evaluations' }) }
                      )
                    }}
                    disabled={deletePitchSessionMutation.isPending}
                  >
                    {deletePitchSessionMutation.isPending ? 'Deleting...' : 'Delete'}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>

            <AlertDialog open={deleteEvaluationOpen} onOpenChange={setDeleteEvaluationOpen}>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete Evaluation</AlertDialogTitle>
                  <AlertDialogDescription>
                    This will permanently delete this evaluation. This action cannot be undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={() => {
                      deleteEvaluationMutation.mutate(
                        { path: { id } },
                        { onSuccess: () => navigate({ to: '/admin/evaluations' }) }
                      )
                    }}
                    disabled={deleteEvaluationMutation.isPending}
                  >
                    {deleteEvaluationMutation.isPending ? 'Deleting...' : 'Delete'}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>
      </div>

      {activeTab === 'overview' && (
        <OverviewTab evaluation={evaluation} dateStr={dateStr} details={details} />
      )}

      {activeTab === 'pitch-session' && (
        <PitchSessionTab details={details} isLoading={detailsQuery.isLoading} />
      )}

      {activeTab === 'research' && (
        <ResearchTab pitchSession={pitchSession} isLoading={detailsQuery.isLoading} />
      )}
    </div>
  )
}

// ─── Overview Tab ───────────────────────────────────────────────────────────

function OverviewTab({
  evaluation,
  dateStr,
  details,
}: {
  evaluation: PitchEvaluationViewProps & { date: string }
  dateStr: string
  details: AdminEvaluationDetails | undefined
}) {
  const videoUrlMutation = useAdminPitchVideoUrlMutation()
  const docDownloadMutation = useAdminDocumentDownloadUrlMutation()
  const [previewDoc, setPreviewDoc] = useState<PreviewDocument | null>(null)
  const [docPreviewOpen, setDocPreviewOpen] = useState(false)

  const ps = details?.pitchSession
  const pr = details?.prescreenRequest
  const pitchDeckId = ps?.pitchDeckId ?? pr?.pitchDeckId

  // Eagerly fetch the video URL so the tile shows an inline preview (same
  // pattern as the startup profile page) instead of a blank placeholder.
  const video = useVideoPreview({
    fetchId: hasRecording(ps) ? ps?.id : null,
    fetchVideoUrl: (id) =>
      videoUrlMutation.mutateAsync({ path: { pitchId: id } }).then((r) => r.data.url),
  })

  const handleOpenPitchDeck = () => {
    if (!pitchDeckId) return
    setPreviewDoc({ id: pitchDeckId, fileName: 'Pitch Deck', mimeType: 'application/pdf' })
    setDocPreviewOpen(true)
  }

  const fetchDocUrl = async (docId: string) => {
    const res = await docDownloadMutation.mutateAsync({ path: { id: docId } })
    if (res.status === 'ok') return res.data.downloadUrl
    throw new Error('Failed to get download URL')
  }

  return (
    <>
      <PitchEvaluationView
        {...evaluation}
        date={dateStr}
        judgeDetailPath={(judgeKey: string) =>
          `/admin/evaluations/${details?.id ?? ''}/judge/${judgeKey}?startupId=${details?.pitchSession?.startupId ?? details?.prescreenRequest?.startupId ?? ''}`
        }
        homePath="/admin/evaluations"
        pitchPath=""
        videoUrl={video.videoUrl}
        videoModalOpen={video.videoOpen}
        videoResumeTime={video.videoResumeTime}
        onPlayRecording={video.videoUrl ? video.handlePlayRecording : undefined}
        onOpenPitchDeck={pitchDeckId ? () => handleOpenPitchDeck() : undefined}
      />
      {video.videoUrl && (
        <VideoPreviewModal
          videoUrl={video.videoUrl}
          open={video.videoOpen}
          onOpenChange={video.setVideoOpen}
          startTime={video.videoStartTime}
          onTimeUpdate={video.setVideoResumeTime}
        />
      )}
      <DocumentPreviewModal
        document={previewDoc}
        open={docPreviewOpen}
        onOpenChange={setDocPreviewOpen}
        fetchUrl={fetchDocUrl}
      />
    </>
  )
}

// ─── Pitch Session Tab ───────────────────────────────────────────────────────

function PitchSessionTab({
  details,
  isLoading,
}: {
  details: AdminEvaluationDetails | undefined
  isLoading: boolean
}) {
  const videoUrlMutation = useAdminPitchVideoUrlMutation()
  const docDownloadMutation = useAdminDocumentDownloadUrlMutation()
  const [videoUrl, setVideoUrl] = useState<string | null>(null)
  const [videoOpen, setVideoOpen] = useState(false)
  const [previewDoc, setPreviewDoc] = useState<PreviewDocument | null>(null)
  const [docPreviewOpen, setDocPreviewOpen] = useState(false)

  const handleOpenVideo = async (pitchSessionId: string) => {
    const res = await videoUrlMutation.mutateAsync({ path: { pitchId: pitchSessionId } })
    if (res.status === 'ok') {
      setVideoUrl(res.data.url)
      setVideoOpen(true)
    }
  }

  const handleOpenPitchDeck = (pitchDeckId: string) => {
    setPreviewDoc({ id: pitchDeckId, fileName: 'Pitch Deck', mimeType: 'application/pdf' })
    setDocPreviewOpen(true)
  }

  const fetchDocUrl = async (docId: string) => {
    const res = await docDownloadMutation.mutateAsync({ path: { id: docId } })
    if (res.status === 'ok') return res.data.downloadUrl
    throw new Error('Failed to get download URL')
  }
  if (isLoading) {
    return (
      <div className="flex justify-center py-10">
        <Loader2 className="size-5 animate-spin text-muted-foreground" />
      </div>
    )
  }
  if (!details) return <div className="py-10 text-center text-muted-foreground">No data</div>

  const ps = details.pitchSession
  const pr = details.prescreenRequest

  return (
    <div className="space-y-6">
      {/* Evaluation info */}
      <section className="space-y-3">
        <h3 className="text-sm font-semibold">Evaluation</h3>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <InfoCard label="Status">
            <Badge variant={STATUS_VARIANT[details.status] ?? 'secondary'} size="sm">
              {details.status}
            </Badge>
          </InfoCard>
          <InfoCard label="Type">
            <Badge variant="secondary" size="sm">
              {details.type}
            </Badge>
          </InfoCard>
          <InfoCard label="Score">
            <span className={`tabular-nums font-semibold ${scoreColor(details.score)}`}>
              {details.score ?? '—'}
            </span>
          </InfoCard>
          <InfoCard label="Created">{formatDateTime(details.createdAt)}</InfoCard>
        </div>
        {details.winnerPersona && (
          <InfoCard label="Winner Persona">{details.winnerPersona}</InfoCard>
        )}
        {(hasRecording(ps) || ps?.pitchDeckId || pr?.pitchDeckId) && (
          <div className="flex gap-2">
            {ps && hasRecording(ps) && (
              <SecondaryButton
                size="sm"
                icon={<Play />}
                loading={videoUrlMutation.isPending}
                onClick={() => handleOpenVideo(ps.id)}
              >
                Watch video
              </SecondaryButton>
            )}
            {(ps?.pitchDeckId || pr?.pitchDeckId) && (
              <SecondaryButton
                size="sm"
                icon={<FileText />}
                onClick={() => {
                  const deckId = ps?.pitchDeckId ?? pr?.pitchDeckId
                  if (deckId) handleOpenPitchDeck(deckId)
                }}
              >
                View pitch deck
              </SecondaryButton>
            )}
          </div>
        )}
        {details.verdict && (
          <div>
            <p className="mb-1 text-xs font-medium text-muted-foreground">Verdict</p>
            <Prose className="text-sm">{details.verdict}</Prose>
          </div>
        )}
      </section>

      {/* Pitch session info */}
      {ps && (
        <section className="space-y-3">
          <h3 className="text-sm font-semibold">Pitch Session</h3>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <InfoCard label="Session Status">
              <Badge variant={STATUS_VARIANT[ps.status] ?? 'secondary'} size="sm">
                {ps.status}
              </Badge>
            </InfoCard>
            <InfoCard label="Startup">{ps.startup?.name ?? '—'}</InfoCard>
            <InfoCard label="Investor">{ps.investor?.name ?? '—'}</InfoCard>
            <InfoCard label="Published">{ps.isPublished ? 'Yes' : 'No'}</InfoCard>
            <InfoCard label="Created">{formatDateTime(ps.createdAt)}</InfoCard>
            <InfoCard label="Finished">{formatDateTime(ps.finishedAt)}</InfoCard>
            {ps.createdAt && ps.finishedAt && (
              <InfoCard label="Duration">
                {Math.round(
                  (new Date(ps.finishedAt).getTime() - new Date(ps.createdAt).getTime()) / 60000
                )}{' '}
                min
              </InfoCard>
            )}
          </div>
        </section>
      )}

      {/* Video modal */}
      {videoUrl && (
        <VideoPreviewModal videoUrl={videoUrl} open={videoOpen} onOpenChange={setVideoOpen} />
      )}

      {/* Document modal */}
      <DocumentPreviewModal
        document={previewDoc}
        open={docPreviewOpen}
        onOpenChange={setDocPreviewOpen}
        fetchUrl={fetchDocUrl}
      />

      {/* Transcript */}
      {ps?.transcript && (
        <section className="rounded-lg border border-border bg-card">
          <div className="border-b border-border px-4 py-2">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Transcript
            </h3>
          </div>
          <div className="px-4 py-3">
            <Prose className="text-sm leading-relaxed">{ps.transcript}</Prose>
          </div>
        </section>
      )}

      {/* Questions */}
      {ps?.questions && (
        <section className="rounded-lg border border-border bg-card">
          <div className="border-b border-border px-4 py-2">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Questions
            </h3>
          </div>
          <div className="px-4 py-3">
            <Prose className="text-sm leading-relaxed">{ps.questions}</Prose>
          </div>
        </section>
      )}

      {/* Pitch Deck Full Text */}
      {details.pitchDeckFullText && (
        <section className="rounded-lg border border-border bg-card">
          <div className="border-b border-border px-4 py-2">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Pitch Deck Full Text
            </h3>
          </div>
          <div className="px-4 py-3">
            <Prose className="text-sm leading-relaxed">{details.pitchDeckFullText}</Prose>
          </div>
        </section>
      )}

      {/* Prescreen info */}
      {pr && !ps && (
        <section className="space-y-3">
          <h3 className="text-sm font-semibold">Prescreen Request</h3>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            <InfoCard label="Status">
              <Badge variant={STATUS_VARIANT[pr.status] ?? 'secondary'} size="sm">
                {pr.status}
              </Badge>
            </InfoCard>
            <InfoCard label="Startup">{pr.startup?.name ?? '—'}</InfoCard>
            <InfoCard label="Investor">{pr.persona?.investor?.name ?? 'Council'}</InfoCard>
          </div>
        </section>
      )}

      {/* Collective metrics */}
      {details.collectiveMetricEvaluations?.length > 0 && (
        <section className="space-y-3">
          <h3 className="text-sm font-semibold">Collective Metrics</h3>
          <div className="grid gap-2 sm:grid-cols-3">
            {details.collectiveMetricEvaluations.map((m: AdminMetricEvaluation) => (
              <div
                key={m.id}
                className="flex items-center justify-between rounded border border-border px-3 py-2"
              >
                <span className="text-xs">{m.metric}</span>
                <span className={`tabular-nums text-xs font-medium ${scoreColor(m.score)}`}>
                  {m.score ?? '—'}
                </span>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Persona evaluations */}
      {details.personaEvaluations?.length > 0 && (
        <section className="space-y-3">
          <h3 className="text-sm font-semibold">
            Persona Evaluations ({details.personaEvaluations.length})
          </h3>
          {details.personaEvaluations.map((pe: AdminPersonaEvaluation) => (
            <div key={pe.id} className="rounded-lg border border-border bg-card p-4 space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium">{pe.persona}</span>
                  {pe.decision && (
                    <Badge
                      variant={
                        pe.decision === 'INVEST'
                          ? 'success'
                          : pe.decision === 'PASS'
                            ? 'destructive'
                            : 'warning'
                      }
                      size="sm"
                    >
                      {pe.decision}
                    </Badge>
                  )}
                </div>
                {pe.score != null && (
                  <span className={`tabular-nums text-sm font-semibold ${scoreColor(pe.score)}`}>
                    {pe.score}
                  </span>
                )}
              </div>
              {pe.decisionThesis && (
                <Prose className="text-sm text-muted-foreground">{pe.decisionThesis}</Prose>
              )}
              {pe.metricEvaluations?.length > 0 && (
                <div className="grid gap-1.5 sm:grid-cols-3">
                  {pe.metricEvaluations.map((me: AdminMetricEvaluation) => (
                    <div
                      key={me.id}
                      className="flex items-center justify-between rounded border border-border px-2 py-1"
                    >
                      <span className="text-[11px]">{me.metric}</span>
                      <span
                        className={`tabular-nums text-[11px] font-medium ${scoreColor(me.score)}`}
                      >
                        {me.score ?? '—'}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </section>
      )}
    </div>
  )
}

// ─── Research Tab ────────────────────────────────────────────────────────────

function ResearchTab({
  pitchSession,
  isLoading,
}: {
  pitchSession: AdminEvaluationPitchSession | null | undefined
  isLoading: boolean
}) {
  if (isLoading) {
    return (
      <div className="flex justify-center py-10">
        <Loader2 className="size-5 animate-spin text-muted-foreground" />
      </div>
    )
  }

  const hasTeam = !!pitchSession?.teamResearch
  const hasMarket = !!pitchSession?.marketResearch

  if (!hasTeam && !hasMarket) {
    return <div className="py-10 text-center text-muted-foreground">No research data available</div>
  }

  return (
    <div className="space-y-6">
      {hasTeam && (
        <section className="rounded-lg border border-border bg-card">
          <div className="border-b border-border px-4 py-2">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Team Research
            </h3>
          </div>
          <div className="px-4 py-3">
            <Prose className="text-sm leading-relaxed">{pitchSession.teamResearch}</Prose>
          </div>
        </section>
      )}
      {hasMarket && (
        <section className="rounded-lg border border-border bg-card">
          <div className="border-b border-border px-4 py-2">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Market Research
            </h3>
          </div>
          <div className="px-4 py-3">
            <Prose className="text-sm leading-relaxed">{pitchSession.marketResearch}</Prose>
          </div>
        </section>
      )}
    </div>
  )
}

function InfoCard({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-border bg-card px-4 py-3">
      <p className="text-xs text-muted-foreground">{label}</p>
      <div className="mt-1 text-sm font-medium">{children}</div>
    </div>
  )
}
